import { Link } from 'react-router-dom'
import { Lightbulb } from 'lucide-react'
import { FaPlayCircle } from 'react-icons/fa'
import type { HackSymptom } from '@domain/models/pd-hacks'

interface HackCardProps {
  hack: HackSymptom
}

export default function HackCard({ hack }: HackCardProps) {
  const hackPath = `/hacks/${hack.uid}`
  const hackTitle = hack.name?.trim() || 'Untitled hack'
  const hasVideoPreview = hack.videoId.trim().length > 0
  const previewThumbnailUrl = hasVideoPreview
    ? `https://i.ytimg.com/vi/${hack.videoId}/hqdefault.jpg`
    : ''

  return (
    <Link
      to={hackPath}
      aria-label={`Open Michel's PD Hack: ${hackTitle}`}
      className="group block overflow-hidden rounded-xl bg-card-surface no-underline shadow-lg shadow-black/20 ring-1 ring-black/5 transition-[transform,box-shadow] duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-black/30"
    >
      {hasVideoPreview ? (
        <div
          className="relative w-full overflow-hidden rounded-t-xl"
          style={{ aspectRatio: '767 / 1365' }}
        >
          <img
            src={previewThumbnailUrl}
            alt={`${hackTitle} preview`}
            className="h-full w-full object-cover"
            loading="lazy"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <FaPlayCircle className="size-10 text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]" />
          </div>
        </div>
      ) : (
        <div
          className="flex w-full items-center justify-center bg-border-light px-4 text-center text-sm font-semibold text-text-muted"
          style={{ aspectRatio: '767 / 1365' }}
        >
          Video preview unavailable
        </div>
      )}

      <div className="p-4">
        <h3 className="text-lg font-bold text-text-primary transition-colors group-hover:text-primary">
          {hackTitle}
        </h3>
        <div
          aria-hidden="true"
          className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-blue px-4 py-3 text-sm font-bold text-white transition-colors group-hover:bg-blue/90"
        >
          <span className="inline-flex items-center justify-center gap-2 text-center leading-tight">
            <Lightbulb size={18} aria-hidden="true" />
            Learn More and Share Your Hack
          </span>
        </div>
      </div>
    </Link>
  )
}
